import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import './Dashboard.css';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('weight-loss'); // 'weight-loss' or 'bulk'
  const [orders, setOrders] = useState([]);
  const [bulkOrders, setBulkOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredBulkOrders, setFilteredBulkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    fetchBulkOrders();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(order => 
        order.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.phone.includes(searchTerm)
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, orders]);

  useEffect(() => {
    if (bulkSearchTerm.trim() === '') {
      setFilteredBulkOrders(bulkOrders);
    } else {
      const filtered = bulkOrders.filter(order => 
        order.order_id.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        order.name.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        order.email.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        order.phone.includes(bulkSearchTerm) ||
        order.sku.toLowerCase().includes(bulkSearchTerm.toLowerCase())
      );
      setFilteredBulkOrders(filtered);
    }
  }, [bulkSearchTerm, bulkOrders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://gokulnair.com/new_project/api/get-orders');
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (err) {
      setError('Failed to fetch orders');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBulkOrders = async () => {
    try {
      setBulkLoading(true);
      const response = await axios.get('https://gokulnair.com/rpo_bulk/api/get-orders');
      
      // Validate and clean the data
      const validatedOrders = response.data.map(order => ({
        id: order.id || Math.random().toString(36).substr(2, 9),
        order_id: order.order_id || 'N/A',
        sku: order.sku || 'N/A',
        qty: order.qty || 'N/A',
        coupon_id: order.coupon_id || null,
        name: order.name || 'N/A',
        email: order.email || 'N/A',
        phone: order.phone || 'N/A',
        shipping_address: order.shipping_address || 'N/A',
        shipping_city: order.shipping_city || 'N/A',
        shipping_state: order.shipping_state || 'N/A',
        shipping_pincode: order.shipping_pincode || 'N/A',
        delivery_type: order.delivery_type || 'N/A',
        delivery_price: order.delivery_price || '0.00',
        payment_type: order.payment_type || 0,
        payment_status: order.payment_status || 0,
        created_at: order.created_at || new Date().toISOString()
      }));
      
      setBulkOrders(validatedOrders);
      setFilteredBulkOrders(validatedOrders);
    } catch (err) {
      setBulkError('Failed to fetch bulk orders');
      console.error('Error fetching bulk orders:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const getPaymentTypeText = (type) => {
    switch (type) {
      case 0: return 'Zelle';
      case 1: return 'Cash App';
      case 2: return 'Stripe';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Confirmed by User';
      case 2: return 'Confirmed by Admin';
      case 3: return 'Rejected by Admin';
      case 4: return 'Rejected by User';
      default: return 'Unknown';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch (status) {
      case 0: return 'status-pending';
      case 1: return 'status-confirmed-user';
      case 2: return 'status-confirmed-admin';
      case 3: return 'status-rejected-admin';
      case 4: return 'status-rejected-user';
      default: return 'status-pending';
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, sku = '', qty = '') => {
    // If status is "Confirmed by Admin" (value 2), show confirmation modal
    if (newStatus === 2) {
      setPendingStatusUpdate({ orderId, newStatus, sku, qty });
      setShowConfirmModal(true);
      return;
    }

    await updatePaymentStatus(orderId, newStatus, sku, qty);
  };

  const handleBulkStatusUpdate = async (orderId, newStatus, sku = '', qty = '') => {
    // If status is "Confirmed by Admin" (value 2), show confirmation modal
    if (newStatus === 2) {
      setPendingStatusUpdate({ orderId, newStatus, sku, qty, isBulk: true });
      setShowConfirmModal(true);
      return;
    }

    await updateBulkPaymentStatus(orderId, newStatus, sku, qty);
  };

  const updatePaymentStatus = async (orderId, newStatus, sku = '', qty = '') => {
    try {
      const response = await axios.post('https://gokulnair.com/new_project/api/update-payment-status', {
        order_id: orderId,
        payment_status: newStatus,
        sku: sku,
        qty: qty
      });
      
      if (response.data.message === 'Payment status updated') {
        // Refresh orders to show updated status
        fetchOrders();
        setSuccessMessage(`Order ${orderId} status updated successfully!`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000); // Hide after 5 seconds
      } else {
        setErrorMessage('Failed to update payment status: ' + (response.data.message || 'Unknown error'));
        setShowErrorMessage(true);
        setTimeout(() => setShowErrorMessage(false), 5000); // Hide after 5 seconds
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      setErrorMessage('Failed to update payment status: ' + (err.response?.data?.message || err.message));
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 5000); // Hide after 5 seconds
    }
  };

  const updateBulkPaymentStatus = async (orderId, newStatus, sku = '', qty = '') => {
    try {
      const response = await axios.post('https://gokulnair.com/rpo_bulk/api/update-payment-status', {
        order_id: orderId,
        payment_status: newStatus,
        sku: sku,
        qty: qty
      });
      
      if (response.data.message === 'Payment status updated') {
        // Refresh bulk orders to show updated status
        fetchBulkOrders();
        setSuccessMessage(`Bulk Order ${orderId} status updated successfully!`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000); // Hide after 5 seconds
      } else {
        setErrorMessage('Failed to update bulk order status: ' + (response.data.message || 'Unknown error'));
        setShowErrorMessage(true);
        setTimeout(() => setShowErrorMessage(false), 5000); // Hide after 5 seconds
      }
    } catch (err) {
      console.error('Error updating bulk order status:', err);
      setErrorMessage('Failed to update bulk order status: ' + (err.response?.data?.message || err.message));
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 5000); // Hide after 5 seconds
    }
  };

  const confirmStatusUpdate = async () => {
    if (pendingStatusUpdate) {
      if (pendingStatusUpdate.isBulk) {
        await updateBulkPaymentStatus(pendingStatusUpdate.orderId, pendingStatusUpdate.newStatus, pendingStatusUpdate.sku, pendingStatusUpdate.qty);
      } else {
        await updatePaymentStatus(pendingStatusUpdate.orderId, pendingStatusUpdate.newStatus, pendingStatusUpdate.sku, pendingStatusUpdate.qty);
      }
      setShowConfirmModal(false);
      setPendingStatusUpdate(null);
    }
  };

  const cancelStatusUpdate = () => {
    setShowConfirmModal(false);
    setPendingStatusUpdate(null);
  };

  const getStatusOptions = (currentStatus) => {
    // Admin can only set: 0 (Pending), 2 (Confirmed by Admin), 3 (Rejected by Admin)
    const options = [
      { value: 0, label: 'Pending' },
      { value: 2, label: 'Confirmed by Admin' },
      { value: 3, label: 'Rejected by Admin' }
    ];
    
    return options.map(option => ({
      ...option,
      selected: option.value === currentStatus
    }));
  };

  if (loading && activeTab === 'weight-loss') {
    return (
      <Layout>
        <div className="loading">Loading orders...</div>
      </Layout>
    );
  }

  if (bulkLoading && activeTab === 'bulk') {
    return (
      <Layout>
        <div className="loading">Loading bulk orders...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard-content">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="success-message">
            <div className="success-icon">✅</div>
            <div className="success-content">
              <h3>Status Updated Successfully!</h3>
              <p>{successMessage}</p>
            </div>
            <button 
              className="close-message-btn"
              onClick={() => setShowSuccessMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        {/* Error Message */}
        {showErrorMessage && (
          <div className="error-message-box">
            <div className="error-icon">❌</div>
            <div className="error-content">
              <h3>Update Failed</h3>
              <p>{errorMessage}</p>
            </div>
            <button 
              className="close-message-btn"
              onClick={() => setShowErrorMessage(false)}
            >
              ×
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="dashboard-tabs">
          <button 
            className={`tab-button ${activeTab === 'weight-loss' ? 'active' : ''}`}
            onClick={() => setActiveTab('weight-loss')}
          >
            RPO Weight Loss
          </button>
          <button 
            className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk')}
          >
            RPO Bulk
          </button>
        </div>

        {/* RPO Weight Loss Tab Content */}
        {activeTab === 'weight-loss' && (
          <>
            <div className="tab-header">
              <h2>RPO Weight Loss Orders</h2>
              <button 
                className="refresh-button"
                onClick={fetchOrders}
                disabled={loading}
              >
                {loading ? '🔄' : '🔄'} Refresh
              </button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📦</div>
                <div className="stat-content">
                  <h3>Total Orders</h3>
                  <p className="stat-number">{orders.length.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <h3>Total Customers</h3>
                  <p className="stat-number">{orders.length.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="search-section">
              <div className="search-container">
                <div className="search-icon">🔍</div>
                <input
                  type="text"
                  placeholder="Search orders by ID, name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="clear-search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="search-results">
                {searchTerm && (
                  <p>Found {filteredOrders.length} order(s) matching "{searchTerm}"</p>
                )}
              </div>
            </div>

            <div className="recent-orders">
              <h2>Recent Orders</h2>
              <div className="orders-table">
                {filteredOrders.length === 0 ? (
                  <div className="no-orders">
                    {searchTerm ? `No orders found matching "${searchTerm}"` : 'No orders found'}
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Payment Type</th>
                        <th>Payment Status</th>
                        <th>Actions</th>
                        <th>Created Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => {
                        const isConfirmedByAdmin = order.payment_status === 2;
                        return (
                          <tr key={order.id} className={isConfirmedByAdmin ? 'confirmed-row' : ''}>
                            <td>
                              <button 
                                className={`order-link ${isConfirmedByAdmin ? 'order-link-confirmed' : ''}`}
                                onClick={() => navigate(`/order/${order.order_id}`)}
                              >
                                {order.order_id}
                              </button>
                            </td>
                            <td>{order.name}</td>
                            <td>{order.email}</td>
                            <td>{order.phone}</td>
                            <td>
                              <span className="payment-type-badge">
                                {getPaymentTypeText(order.payment_type)}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${getPaymentStatusClass(order.payment_status)}`}>
                                {getPaymentStatusText(order.payment_status)}
                              </span>
                            </td>
                            <td>
                              <div className="status-actions">
                                {isConfirmedByAdmin ? (
                                  <select 
                                    className="status-dropdown-disabled"
                                    value={order.payment_status}
                                    disabled
                                  >
                                    <option value={order.payment_status}>
                                      {getPaymentStatusText(order.payment_status)}
                                    </option>
                                  </select>
                                ) : (
                                  <select 
                                    className="status-dropdown"
                                    value={order.payment_status}
                                    onChange={(e) => handleStatusUpdate(order.order_id, parseInt(e.target.value), order.sku || '', order.qty || '')}
                                  >
                                    {getStatusOptions(order.payment_status).map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </td>
                            <td>{new Date(order.created_at).toLocaleDateString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {/* RPO Bulk Tab Content */}
        {activeTab === 'bulk' && (
          <>
            <div className="tab-header">
              <h2>RPO Bulk Orders</h2>
              <button 
                className="refresh-button"
                onClick={fetchBulkOrders}
                disabled={bulkLoading}
              >
                {bulkLoading ? '🔄' : '🔄'} Refresh
              </button>
            </div>
            
            {bulkError && <div className="error-message">{bulkError}</div>}
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📦</div>
                <div className="stat-content">
                  <h3>Total Bulk Orders</h3>
                  <p className="stat-number">{bulkOrders.length.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <h3>Total Customers</h3>
                  <p className="stat-number">{bulkOrders.length.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="search-section">
              <div className="search-container">
                <div className="search-icon">🔍</div>
                <input
                  type="text"
                  placeholder="Search bulk orders by ID, name, email, phone, or SKU..."
                  value={bulkSearchTerm}
                  onChange={(e) => setBulkSearchTerm(e.target.value)}
                  className="search-input"
                />
                {bulkSearchTerm && (
                  <button 
                    onClick={() => setBulkSearchTerm('')}
                    className="clear-search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="search-results">
                {bulkSearchTerm && (
                  <p>Found {filteredBulkOrders.length} bulk order(s) matching "{bulkSearchTerm}"</p>
                )}
              </div>
            </div>

            <div className="recent-orders">
              <h2>Bulk Orders</h2>
              <div className="orders-table bulk-orders">
                {filteredBulkOrders.length === 0 ? (
                  <div className="no-orders">
                    {bulkSearchTerm ? `No bulk orders found matching "${bulkSearchTerm}"` : 'No bulk orders found'}
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Payment Status</th>
                        <th>Actions</th>
                        <th>Created Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBulkOrders.map((order) => {
                        const isConfirmedByAdmin = order.payment_status === 2;
                        return (
                          <tr key={order.id} className={isConfirmedByAdmin ? 'confirmed-row' : ''}>
                            <td>
                              <button 
                                className="order-link"
                                onClick={() => navigate(`/bulk-order/${order.order_id}`)}
                              >
                                {order.order_id}
                              </button>
                            </td>
                            <td>{order.name}</td>
                            <td>{order.email}</td>
                            <td>{order.phone}</td>
                            <td>
                              <span className={`status-badge ${getPaymentStatusClass(order.payment_status)}`}>
                                {getPaymentStatusText(order.payment_status)}
                              </span>
                            </td>
                            <td>
                              <div className="status-actions">
                                {isConfirmedByAdmin ? (
                                  <select 
                                    className="status-dropdown-disabled"
                                    value={order.payment_status}
                                    disabled
                                  >
                                    <option value={order.payment_status}>
                                      {getPaymentStatusText(order.payment_status)}
                                    </option>
                                  </select>
                                ) : (
                                  <select 
                                    className="status-dropdown"
                                    value={order.payment_status}
                                    onChange={(e) => handleBulkStatusUpdate(order.order_id, parseInt(e.target.value), order.sku || '', order.qty || '')}
                                  >
                                    {getStatusOptions(order.payment_status).map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </td>
                            <td>{new Date(order.created_at).toLocaleDateString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Status Update</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to change the payment status to "Confirmed by Admin"?</p>
              <p><strong>Order Type:</strong> {pendingStatusUpdate?.isBulk ? 'Bulk Order' : 'Weight Loss Order'}</p>
              <p><strong>Order ID:</strong> {pendingStatusUpdate?.orderId}</p>
              {pendingStatusUpdate?.sku && (
                <p><strong>SKU:</strong> {pendingStatusUpdate.sku}</p>
              )}
              {pendingStatusUpdate?.qty && (
                <p><strong>Quantity:</strong> {pendingStatusUpdate.qty}</p>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel"
                onClick={cancelStatusUpdate}
              >
                Cancel
              </button>
              <button 
                className="modal-btn modal-btn-confirm"
                onClick={confirmStatusUpdate}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard; 